import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { DeviceTypes } from '@flashbacktech/flashbackclient/api';

/**
 * Device fingerprinting utility using FingerprintJS
 * Collects comprehensive device information for security and session management
 * Uses the existing DeviceTypes.DeviceInfo interface from the API package
 */
export class DeviceFingerprinter {
  private static instance: DeviceFingerprinter;
  private fpPromise: Promise<unknown> | null = null;

  private constructor() {
    // Initialize FingerprintJS
    this.fpPromise = FingerprintJS.load();
  }

  public static getInstance(): DeviceFingerprinter {
    if (!DeviceFingerprinter.instance) {
      DeviceFingerprinter.instance = new DeviceFingerprinter();
    }
    return DeviceFingerprinter.instance;
  }

  /**
   * Collect device information using FingerprintJS and browser APIs
   * Returns data in the format expected by the backend DeviceTypes.DeviceInfo interface
   */
  public async collectDeviceInfo(): Promise<DeviceTypes.DeviceInfo> {
    try {
      // Collect device information according to DeviceTypes.DeviceInfo interface
      const deviceInfo: DeviceTypes.DeviceInfo = {
        userAgent: navigator.userAgent,
        deviceType: this.detectDeviceType(),
        os: this.detectOS(),
        browser: this.detectBrowser(),
        screenResolution: `${screen.width}x${screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        platform: navigator.platform,
        hardwareConcurrency: navigator.hardwareConcurrency || 0,
        deviceMemory: (navigator as { deviceMemory?: number }).deviceMemory || 0,
      };

      return deviceInfo;
    } catch (error) {
      return this.getBasicDeviceInfo();
    }
  }

  /**
   * Fallback method for basic device information collection
   */
  private getBasicDeviceInfo(): DeviceTypes.DeviceInfo {
    return {
      userAgent: navigator.userAgent,
      deviceType: this.detectDeviceType(),
      os: this.detectOS(),
      browser: this.detectBrowser(),
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      hardwareConcurrency: navigator.hardwareConcurrency || 0,
      deviceMemory: (navigator as { deviceMemory?: number }).deviceMemory || 0,
    };
  }

  /**
   * Detect device type from user agent
   */
  private detectDeviceType(): 'DESKTOP' | 'MOBILE' | 'TABLET' {
    const ua = navigator.userAgent.toLowerCase();

    if (/tablet|ipad|playbook|silk/i.test(ua)) {
      return 'TABLET';
    }

    if (/mobile|android|iphone|ipod|blackberry|opera mini|iemobile/i.test(ua)) {
      return 'MOBILE';
    }

    return 'DESKTOP';
  }

  /**
   * Detect operating system from user agent
   */
  private detectOS(): string {
    const userAgent = navigator.userAgent;

    if (/Windows/i.test(userAgent)) {
      if (/Windows NT 10.0/i.test(userAgent)) return 'Windows 10';
      if (/Windows NT 6.3/i.test(userAgent)) return 'Windows 8.1';
      if (/Windows NT 6.2/i.test(userAgent)) return 'Windows 8';
      if (/Windows NT 6.1/i.test(userAgent)) return 'Windows 7';
      return 'Windows';
    }

    if (/Mac OS X/i.test(userAgent)) {
      const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
      if (match) return `macOS ${match[1].replace('_', '.')}`;
      return 'macOS';
    }

    if (/Linux/i.test(userAgent)) {
      if (/Android/i.test(userAgent)) return 'Android';
      if (/Ubuntu/i.test(userAgent)) return 'Ubuntu';
      if (/Fedora/i.test(userAgent)) return 'Fedora';
      return 'Linux';
    }

    if (/iOS/i.test(userAgent)) return 'iOS';
    if (/BlackBerry/i.test(userAgent)) return 'BlackBerry';

    return 'Unknown';
  }

  /**
   * Detect browser from user agent
   */
  private detectBrowser(): string {
    const userAgent = navigator.userAgent;

    if (/Chrome/i.test(userAgent) && !/Edge/i.test(userAgent)) {
      const match = userAgent.match(/Chrome\/(\d+)/);
      if (match) return `Chrome ${match[1]}`;
      return 'Chrome';
    }

    if (/Firefox/i.test(userAgent)) {
      const match = userAgent.match(/Firefox\/(\d+)/);
      if (match) return `Firefox ${match[1]}`;
      return 'Firefox';
    }

    if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) {
      const match = userAgent.match(/Version\/(\d+)/);
      if (match) return `Safari ${match[1]}`;
      return 'Safari';
    }

    if (/Edge/i.test(userAgent)) {
      const match = userAgent.match(/Edge\/(\d+)/);
      if (match) return `Edge ${match[1]}`;
      return 'Edge';
    }

    if (/Opera/i.test(userAgent)) {
      const match = userAgent.match(/Opera\/(\d+)/);
      if (match) return `Opera ${match[1]}`;
      return 'Opera';
    }

    return 'Unknown';
  }

  /**
   * Get device information for authentication requests
   * This is the main method used by auth components
   * Note: Backend handles geolocation automatically using IP address
   */
  public async getDeviceInfoForAuth(): Promise<DeviceTypes.DeviceInfo> {
    return this.collectDeviceInfo();
  }
}

/**
 * Hook for React components to use device fingerprinting
 */
export const useDeviceFingerprint = () => {
  const fingerprinter = DeviceFingerprinter.getInstance();

  return {
    collectDeviceInfo: () => fingerprinter.collectDeviceInfo(),
    getDeviceInfoForAuth: () => fingerprinter.getDeviceInfoForAuth(),
  };
};

export default DeviceFingerprinter;
